import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { Builder } from '@builder.io/react';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { useAccounts, useCurrentAccount, useSession, useUserProfile, } from '../../data';
function isNullOrUndefined(value) {
    return typeof value === 'undefined' || value === null;
}
export var DATASET_LABELS;
(function (DATASET_LABELS) {
    DATASET_LABELS["allAccounts"] = "All Accounts";
    DATASET_LABELS["currentAccount"] = "Current Account";
    DATASET_LABELS["global"] = "Global";
    DATASET_LABELS["queryParameters"] = "Query Parameters";
    DATASET_LABELS["session"] = "User";
    DATASET_LABELS["userProfile"] = "User Profile";
})(DATASET_LABELS || (DATASET_LABELS = {}));
export var COMPARISONS;
(function (COMPARISONS) {
    COMPARISONS["isTrue"] = "is true";
    COMPARISONS["isFalse"] = "is false";
    COMPARISONS["equals"] = "equals";
    COMPARISONS["doesNotEqual"] = "does not equal";
    COMPARISONS["contains"] = "contains";
    COMPARISONS["doesNotContain"] = "does not contain";
    COMPARISONS["startsWith"] = "starts with";
    COMPARISONS["doesNotStartWith"] = "does not start with";
    COMPARISONS["endsWith"] = "ends with";
    COMPARISONS["doesNotEndWith"] = "does not end with";
})(COMPARISONS || (COMPARISONS = {}));
function someConditionUsesDataset(datasetLabel, conditions) {
    return conditions.some((condition) => {
        var _a;
        return (((_a = condition.property) === null || _a === void 0 ? void 0 : _a.startsWith(`${datasetLabel} - `)) ||
            (condition.and && someConditionUsesDataset(datasetLabel, condition.and)));
    });
}
/** Gets a value in a nested object using the provided array of keys */
function getValue(obj, keys) {
    const [firstKey, ...remainingKeys] = keys;
    if (!Object.hasOwn(obj !== null && obj !== void 0 ? obj : {}, firstKey)) {
        return null;
    }
    const value = obj[firstKey];
    if (remainingKeys.length === 0) {
        return value;
    }
    return getValue(value, remainingKeys);
}
/** @deprecated - ConditionalVisibility should be used instead */
export function Conditional({ conditions = [], preview, children, }) {
    const router = useRouter();
    const shouldFetch = {
        allAccounts: someConditionUsesDataset(DATASET_LABELS.allAccounts, conditions),
        currentAccount: someConditionUsesDataset(DATASET_LABELS.currentAccount, conditions),
        session: someConditionUsesDataset(DATASET_LABELS.session, conditions),
        userProfile: someConditionUsesDataset(DATASET_LABELS.userProfile, conditions),
    };
    const allAccounts = useAccounts(shouldFetch.allAccounts);
    const currentAccount = useCurrentAccount(shouldFetch.currentAccount);
    const session = useSession(shouldFetch.session);
    const userProfile = useUserProfile(shouldFetch.userProfile);
    const data = {
        global: {
            is_loading: allAccounts.isLoading ||
                currentAccount.isLoading ||
                session.isLoading ||
                userProfile.isLoading,
        },
        allAccounts: Object.assign(Object.assign({}, (allAccounts.data && {
            plan_info: {
                ids: allAccounts.data.map((account) => account.plan_info.id),
                names: allAccounts.data.map((account) => account.plan_info.name),
            },
        })), { is_loading: allAccounts.isLoading }),
        currentAccount: Object.assign(Object.assign({}, currentAccount.data), { is_loading: currentAccount.isLoading }),
        session: Object.assign(Object.assign({}, session.data), { is_loading: session.isLoading }),
        userProfile: Object.assign(Object.assign({}, userProfile.data), { is_loading: userProfile.isLoading }),
        queryParameters: router.query,
    };
    const conditionIsSatisfied = (condition) => {
        if (!condition.property || !condition.comparison) {
            return false;
        }
        const [datasetLabel, ...keys] = condition.property.split(' - ');
        const datasetKey = {
            [DATASET_LABELS.allAccounts]: 'allAccounts',
            [DATASET_LABELS.currentAccount]: 'currentAccount',
            [DATASET_LABELS.session]: 'session',
            [DATASET_LABELS.userProfile]: 'userProfile',
            [DATASET_LABELS.queryParameters]: 'queryParameters',
            [DATASET_LABELS.global]: 'global',
        }[datasetLabel];
        if (!datasetKey) {
            return false;
        }
        const actualValue = getValue(data, [datasetKey, ...keys]);
        const expectedValue = `${condition.value}`;
        switch (condition.comparison) {
            case COMPARISONS.isTrue:
                return !!actualValue;
            case COMPARISONS.isFalse:
                return !actualValue;
            case COMPARISONS.equals:
                return (!isNullOrUndefined(actualValue) && `${actualValue}` === expectedValue);
            case COMPARISONS.doesNotEqual:
                return (isNullOrUndefined(actualValue) || `${actualValue}` !== expectedValue);
            case COMPARISONS.contains:
                return !isNullOrUndefined(actualValue) && Array.isArray(actualValue)
                    ? actualValue.includes(expectedValue)
                    : `${actualValue}`.includes(expectedValue);
            case COMPARISONS.doesNotContain:
                return (isNullOrUndefined(actualValue) ||
                    !(Array.isArray(actualValue)
                        ? actualValue.includes(expectedValue)
                        : `${actualValue}`.includes(expectedValue)));
            case COMPARISONS.startsWith:
                return (!isNullOrUndefined(actualValue) &&
                    `${actualValue}`.startsWith(expectedValue));
            case COMPARISONS.doesNotStartWith:
                return (isNullOrUndefined(actualValue) ||
                    !`${actualValue}`.startsWith(expectedValue));
            case COMPARISONS.endsWith:
                return (!isNullOrUndefined(actualValue) &&
                    `${actualValue}`.endsWith(expectedValue));
            case COMPARISONS.doesNotEndWith:
                return (isNullOrUndefined(actualValue) ||
                    !`${actualValue}`.endsWith(expectedValue));
            default:
                return false;
        }
    };
    const someConditionIsSatisfied = conditions === null || conditions === void 0 ? void 0 : conditions.some((condition) => {
        return (conditionIsSatisfied(condition) &&
            (condition.and || []).every((andCondition) => {
                return conditionIsSatisfied(andCondition);
            }));
    });
    /**
     * Render nothing if there are no children, the page is being
     * previewed/edited and `preview` is disabled, or the page is not being
     * previewed/edited and no conditions are satisfied
     * */
    if (!children ||
        (Builder.isEditing || Builder.isPreviewing
            ? !preview
            : !someConditionIsSatisfied)) {
        return null;
    }
    return _jsx(Fragment, { children: children });
}
//# sourceMappingURL=Conditional.js.map